import React, { useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useSiteData } from '../context/SiteDataContext.jsx'
import { excerptFromHtml, getCmsPageText, parseCmsAccordions } from '../lib/cms.js'
import { Container, Card, SectionHeading, ButtonLink } from '../components/atoms.jsx'
import { PageHero, RelatedStories, RichContent, SeoManager, ContactForm } from '../components/content.jsx'
import { NotFoundPage } from './NotFoundPage.jsx'

const HERBAL_ACCORDIONS = [
  {
    title: "Urterne var opdelt i 3 grupper",
    description: (
      <div className="space-y-4">
        <ul className="list-disc pl-5 space-y-2 text-stone-600">
          <li>Urter der nærer livet.</li>
          <li>Urter der nærer det naturlige og vitaliteten</li>
          <li>Gifte eller medicin fra giftplanter til bekæmpelse af de mest alvorlige sygdomme</li>
        </ul>
        <p className="text-stone-600 leading-relaxed">
          Omkring år 200 f.Kr. skrev dr. Zhang Zhong Jing den mest berømte afhandling i Han-dynastiet, nemlig ”Shang Han Lun” Diskussion om ubalancer forårsaget af kulde.
        </p>
        <p className="text-stone-600 leading-relaxed">
          Der var mere end 300 recepter og mange af dem bruges stadig den dag i dag.
        </p>
        <p className="text-stone-600 leading-relaxed">
          Dr. Li Shizhen (1517-1593), Ming-dynastiets berømte læge arbejdede i 27 år på sit klassiske Plante- og Urteleksikon (Ben Cao Gang Mu), som den dag i dag forbliver det vigtigste referenceværk
        </p>
      </div>
    )
  },
  {
    title: "Bestemmelser af urters kvaliteter",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Urtemedicin har sin oprindelse i både den taoistiske og buddhistiske tradition. Ved hjælp af meditation og chi kung øvelser observerede man virkningerne af at tage en enkelt urt. På den måde kunne man bestemme urtens kvalitet med hensyn til energi.
        </p>
        <p>
          Var urtens energi varmende eller kølende, var retningen opad eller nedad, hvilket organ eller legemsdel gik urtens energi til. De blev også opdelt efter smag, sød, sur, bitter, skarp og salt, hver smag relaterer til et element i de 5 elementer (Wu Xing).
        </p>
        <p>
          Ved hjælp af metodiske studier var man i stand til at samle oplysninger om planter/urter, deres rødder, stængler, kviste, bark, blade, blomster og frø. Mineraler, skaller og dele fra dyreverden, så som insekter og f.eks. hjortetaksekstrakt (Lu Rong) som er meget varmende energi til nyrerne, blev også undersøgt på denne måde.
        </p>
      </div>
    )
  },
  {
    title: "Hvordan sammensættes en recept?",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Normalt bruger kineserne ikke en urt alene, man blander flere urter sammen, for på den måde at få en stærkere og mere afbalanceret energi, urterne støtter hinanden efter bestemte mønstre.
        </p>
        <p>
          Der vælges en <strong className="text-forest-900">kejser urt</strong>, som skal bestemme hvilket organ der skal påvirkes med enten varmende eller kølende energi.
        </p>
        <p>
          Der vælges en <strong className="text-forest-900">minister eller rådgiver</strong> til at understøtte kejser urten.
        </p>
        <p>
          Der vælges en <strong className="text-forest-900">vejviser</strong>, som bestemmer hvilken retning energien skal have opad eller nedad, indad eller udad.
        </p>
        <p>
          Der vælges en <strong className="text-forest-900">kontrollant</strong> som skal sørge for at urteblandingen ikke bliver for voldsom, men lidt mere afbalanceret.
        </p>
        <p>
          Urteblandingerne kan f.eks. være meget varmende, let varmende, neutrale, kølende, kolde, fugtende, tørre.
        </p>
        <p>
          Ligesom akupunktur er urtemedicin en meget gammel og anerkendt behandlingsform i hele østen.
        </p>
        <p>
          Man benyttede urter hvis kostændring, motion (ofte i form af Tai Chi øvelser og Chi kung), massage og meditation ikke havde hjulpet tilstrækkeligt. Urtemedicin er en meget kraftfuld behandlingsform, bruges den rigtigt, kan den virkelig støtte og balancere kroppens energi, blodet og kropsvæskerne. Urtemedicinen bliver på den måde en udvidelse af vores kost, meget nærende for kroppen, og sætter ekstra skub i et lidt langsommeligt behandlingsforløb.
        </p>
        <p>
          I Kina har ethvert hospital med respekt for sig selv også en TKM afdeling med dertil hørende urteapotek. En kinesisk urtekyndig har mere end 3000 substanser til rådighed, det er ikke kun plante dele, men også mineraler, dyr, skaller og insekter mm. Det er meget almindeligt at ligge inde med mere end 300 forskellige urter på lageret. Nogle steder opbevarer man stadig urterne i gamle træskuffer og kæmpe lerkrukker.
        </p>
      </div>
    )
  },
  {
    title: "Hvad bruges urterne til?",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          I Kina forskes der i dag rigtig meget i virkningen af urtemedicin, urterne anvendes til at behandle en masse forskellige sygdomme lige fra almindelige forkølelser til kræftbehandling. Der er lavet forskellige patent opskrifter på behandling af f.eks. bivirkninger af kemokur og strålebehandling, akutte infektioner og kroniske, degenerative sygdomme.
        </p>
        <p>
          Det anerkendes i almindelighed at kinesisk medicin kan hjælpe mod mere end 40 navngivne sygdomme. Se liste under “Hvilke lidelser behandles”.
        </p>
      </div>
    )
  },
  {
    title: "Giftige planter",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Visse steder i Vesten er man bekymret for visse urters giftighed. Det er hel korrekt at nogle af urterne er giftige og har en meget kraftig virkning, men det er jo også derfor de aldrig gives alene, men altid sammen med en hel del andre urter i nøje afbalancerede opskrifter. I Nogle opskrifter har man afgiftet urterne ved en særlig tilberedningsmetode f.eks. iblødsætning i ingefærsaft.
        </p>
      </div>
    )
  },
  {
    title: "Hvordan indtages urterne?",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Urter og urteopskrifter kan fås på mange måder. I Kina bruges mest at lave almindelig te af en urteblanding. Nogle gange koges de rå tørrede urter i vand alene eller i en blanding af vin og vand. Varigheden af kogningen afhænger af, hvad det er man koger. Generelt siger man at tunge og hårde urter skal koge længere end de lette og bløde urter, ofte tilsætter man nogle særlige urter til sidst, for at de ikke skal have kogt energien væk.
        </p>
        <p>
          Urteudtræk som te eller efter kogning har en meget fin optagelse i kroppen og reaktionerne kommer meget hurtigere end hvis man spiser urterne som f.eks. pulver eller tabletter. En kold person vil f.eks. føle varmen sprede sig i løbet af kort tid.
        </p>
        <p>
          Smagen kan nogle gange være det, der hedder en tilvænningssag. Lugten og smagen kan ved sarte mennesker nogle gange give dem lidt kvalme især i starten af et behandlingsforløb.
        </p>
        <p>
          Vi kan rådgive dig i hvilke patent urteblandinger der kan gavne dig og din situation, og hvilke du skal holde fingrene langt fra. Det er idag ulovligt at sælge kinesisk urtemedicin i Danmark, men du må gerne bestille det hjem fra udlandet til eget forbrug. Her kan vi være dig behjælpelig – nogle firmaer ønsker ganske enkelt at få oplyst din behandler for sikre sig at du er godt informeret.
        </p>
      </div>
    )
  }
]

const FALLBACK_BEHANDLINGSMETODER = [
  {
    title: 'Traditionel Kinesisk medicin (T.K.M.)',
    description: 'Historien, filosofien og principperne bag over 4.000 års kinesisk lægekunst.',
    link: '/behandlingsmetoder/traditionel-kinesisk-medicin-t-k-m',
    order: 1
  },
  {
    title: 'Klassisk Kinesisk akupunktur',
    description: 'Stimulering af akupunkturpunkter på meridianerne for at genoprette kroppens energibalance.',
    link: '/behandlingsmetoder/akupunktur',
    order: 2
  },
  {
    title: 'Kinesisk urtemedicin',
    description: 'Kinesisk farmakopé og sammensætning af recepter med naturlige lægeplanter.',
    link: '/behandlingsmetoder/kinesisk-urtemedicin',
    order: 3
  },
  {
    title: 'Chi-Kung (energitræning)',
    description: 'Zhan Zhuang Chi Kung – stående meditation og øvelser for at opbygge kroppens energi.',
    link: '/behandlingsmetoder/chi-kung-energitraening',
    order: 4
  },
  {
    title: 'Chi-Kung Holdtræning & Workshops',
    description: 'Holdtræning og workshops i Zhan Zhuang Chi Kung på Helseklinikken i Gjerrild.',
    link: '/behandlingsmetoder/chi-kung-workshop',
    order: 5
  },
  {
    title: 'Tai Chi Chuan',
    description: 'Bevægende meditation og kropslig harmoni med stille og rolige bevægelser.',
    link: '/behandlingsmetoder/tai-chi-chuan',
    order: 6
  },
  {
    title: 'Øreakupunktur',
    description: 'Fransk og kinesisk øreakupunktur samt NADA for hurtig smerte- og afvænningsterapi.',
    link: '/behandlingsmetoder/oereakupunktur',
    order: 7
  },
  {
    title: 'Posturologi',
    description: 'Neurofysiologisk behandling af holdning, smerter og balanceforstyrrelser.',
    link: '/behandlingsmetoder/posturologi',
    order: 8
  },
  {
    title: 'Laserterapi',
    description: 'Smertefri lysbehandling med Power Laser der stimulerer cellefornyelse.',
    link: '/behandlingsmetoder/laserterapi',
    order: 9
  },
  {
    title: 'Magnetfelt Terapi',
    description: 'Pulserende magnetfelter der forbedrer cellernes stofskifte og iltoptagelse.',
    link: '/behandlingsmetoder/magnetfelt-terapi',
    order: 10
  },
  {
    title: 'TENS',
    description: 'Transkutan Elektrisk Nerve Stimulation til effektiv smertelindring.',
    link: '/behandlingsmetoder/tens',
    order: 11
  },
  {
    title: 'E.F.T. – Banke Akupunktur',
    description: 'Emotional Freedom Technique (Tapping) mod fysiske og følelsesmæssige blokeringer.',
    link: '/behandlingsmetoder/e-f-t-banke-akupunktur',
    order: 12
  },
  {
    title: 'Vægttab på den nemme måde',
    description: 'Simpelt program kombineret af øreakupunktur, åndedrætsøvelser og enkle leveregler.',
    link: '/behandlingsmetoder/vaegttab-paa-den-nemme-maade',
    order: 13
  }
]

const FALLBACK_SYGDOMME_LIDELSER = [
  {
    title: 'Akillessenen – smerter i akillessenen',
    description: 'Akupunktur og laserbehandling for smertefuld akillessene.',
    link: '/sygdomme-lidelser/akillessen-smerter-i-akillessenen',
    order: 1
  },
  {
    title: 'Albue smerter',
    description: 'Akupunktur er effektiv mod albue smerter (tennisalbue, golfalbue eller musearm).',
    link: '/sygdomme-lidelser/albue-smerter-tennisalbue-golfalbue-eller-musearm',
    order: 2
  },
  {
    title: 'Allergi – helårs allergi',
    description: 'Naturlig lindring af symptomer ved helårsallergi.',
    link: '/sygdomme-lidelser/allergi-helaars-allergi',
    order: 3
  },
  {
    title: 'Angst nær Aarhus',
    description: 'Akupunktur og åndedrætsøvelser mod angst.',
    link: '/sygdomme-lidelser/angst',
    order: 4
  },
  {
    title: 'Astma – hoste',
    description: 'Akupunktur og Chi Kung er meget effektiv i behandling af astma og hoste.',
    link: '/sygdomme-lidelser/astma-hoste',
    order: 5
  },
  {
    title: 'Barnløshed',
    description: 'Naturlig støtte til barnløse par og fertilitetsbehandling.',
    link: '/sygdomme-lidelser/barnloeshed-fertilitetsproblemer',
    order: 6
  },
  {
    title: 'Bihule- og kæbebetændelse (Sinuitis)',
    description: 'Akupunktur er effektivt ved bihulebetændelse og kæbebetændelse.',
    link: '/sygdomme-lidelser/bihule-og-kaebebetaendelse-sinuitis',
    order: 7
  },
  {
    title: 'Diskusprolaps',
    description: 'Akupunktur og blide øvelser mod diskusprolaps og rygsmerter.',
    link: '/sygdomme-lidelser/diskusprolaps',
    order: 8
  },
  {
    title: 'Eksem – hudlidelser',
    description: 'Naturlig løsning mod eksem, hudlidelser og nældefeber.',
    link: '/sygdomme-lidelser/eksem-hudlidelser',
    order: 9
  },
  {
    title: 'Fertilitetsproblemer',
    description: 'Akupunktur understøtter fertiliteten og øger chancen for graviditet.',
    link: '/sygdomme-lidelser/barnloeshed-fertilitetsproblemer',
    order: 10
  },
  {
    title: 'Fibersprængning',
    description: 'Hurtigere opheling og smertelindring ved fibersprængninger.',
    link: '/sygdomme-lidelser/fiberspraengning',
    order: 11
  },
  {
    title: 'Fod og ankelproblemer',
    description: 'Effektiv akupunkturbehandling ved fod- og ankelproblemer.',
    link: '/sygdomme-lidelser/fod-og-ankel-problemer',
    order: 12
  },
  {
    title: 'Fordøjelses problemer',
    description: 'Effektiv akupunkturbehandling af fordøjelsesgener og mave-tarmlidelser.',
    link: '/sygdomme-lidelser/fordoejelses-problemer-mave-og-tarmlidelser',
    order: 13
  },
  {
    title: 'Frossen skulder – skuldersmerter',
    description: 'Effektiv akupunkturbehandling mod frossen skulder og skuldersmerter.',
    link: '/sygdomme-lidelser/frossen-skulder-skuldersmerter',
    order: 14
  },
  {
    title: 'Golfalbue',
    description: 'Målrettet behandling af golfalbue og relaterede overbelastningsskader.',
    link: '/sygdomme-lidelser/albue-smerter-tennisalbue-golfalbue-eller-musearm',
    order: 15
  },
  {
    title: 'Herpes Simplex Forkølelsessår',
    description: 'Akupunkturbehandling af forkølelsessår (HSV 1).',
    link: '/sygdomme-lidelser/herpes-simplex-forkoelelsessaar',
    order: 16
  },
  {
    title: 'Herpes Zoster Helvedesild',
    description: 'Behandling af Herpes Zoster (Helvedes Ild) med akupunktur.',
    link: '/sygdomme-lidelser/herpes-zoster-helvedesild',
    order: 17
  },
  {
    title: 'Hofte smerter',
    description: 'Akupunktur er en god behandlingsmetode for hoftesmerter.',
    link: '/sygdomme-lidelser/hofte-smerter',
    order: 18
  },
  {
    title: 'Hortons hovedpine',
    description: 'Akupunktur mod Hortons hovedpine og svære smerter.',
    link: '/sygdomme-lidelser/hovedpine-migraene-og-hortons-hovepine',
    order: 19
  },
  {
    title: 'Hovedpine og migræne',
    description: 'Akupunktur mod hovedpine og migræne nær Aarhus.',
    link: '/sygdomme-lidelser/hovedpine-migraene-og-hortons-hovepine',
    order: 20
  },
  {
    title: 'Hudlidelser',
    description: 'Behandling af eksem, psoriasis og andre hudproblemer.',
    link: '/sygdomme-lidelser/eksem-hudlidelser',
    order: 21
  },
  {
    title: 'Hælspore',
    description: 'Akupunkturbehandling og aflastning af hælspore.',
    link: '/sygdomme-lidelser/haelspore',
    order: 22
  },
  {
    title: 'Høfeber/pollenallergi',
    description: 'Akupunktur mod høfeber og pollenallergi (sæsonbetonet allergi).',
    link: '/sygdomme-lidelser/hoefeber-pollenallergi-saesonbetonet-allergi',
    order: 23
  },
  {
    title: 'Idrætsskader',
    description: 'Effektiv behandling af sports- og idrætsskader.',
    link: '/sygdomme-lidelser/sportsskader-idraetsskader',
    order: 24
  },
  {
    title: 'Immunforsvar',
    description: 'Styrkelse af immunforsvaret og øget modstandskraft.',
    link: '/sygdomme-lidelser/immunforsvar',
    order: 25
  },
  {
    title: 'Iskias smerter',
    description: 'Akupunktur er effektiv ved iskias og udstrålende smerter.',
    link: '/sygdomme-lidelser/iskias-smerter',
    order: 26
  },
  {
    title: 'Knæ smerter',
    description: 'Slidgigt, meniskskade, ledbåndsskade, korsbåndskader, springerknæ og Osgood Schlatters syndrom.',
    link: '/sygdomme-lidelser/knae-smerter',
    order: 27
  },
  {
    title: 'KOL',
    description: 'Behandling af KOL (Kronisk obstruktiv lungesygdom – også kaldt rygerlunger) nær Aarhus.',
    link: '/sygdomme-lidelser/kol',
    order: 28
  },
  {
    title: 'Korsbåndsskader i knæet',
    description: 'Genoptræning og smertelindring ved korsbåndsskader.',
    link: '/sygdomme-lidelser/korsbaand-skader-i-knaeet',
    order: 29
  },
  {
    title: 'Kræftbehandling',
    description: 'Akupunktur hjælper rigtig mange kræftpatienter med bivirkninger fra kemo- og strålebehandling.',
    link: '/sygdomme-lidelser/kraeftbehandling-bivirkninger-fra-kemo-og-straalebehandling',
    order: 30
  },
  {
    title: 'Kvalme',
    description: 'Akupunktur mod graviditetskvalme, transportsyge og anden kvalme.',
    link: '/sygdomme-lidelser/kvalme',
    order: 31
  },
  {
    title: 'Ledbåndsskader',
    description: 'Hurtigere heling og stabilitet ved ledbåndsskader i knæ eller ankel.',
    link: '/sygdomme-lidelser/ledbaandsskader-i-knaeet',
    order: 32
  },
  {
    title: 'Leddegigt',
    description: 'Smertelindring og immunforsvarsbalance ved leddegigt.',
    link: '/sygdomme-lidelser/leddegigt',
    order: 33
  },
  {
    title: 'Mave og tarmlidelser',
    description: 'Behandling af irritabel tyktarm, oppustethed og mavekramper.',
    link: '/sygdomme-lidelser/fordoejelses-problemer-mave-og-tarmlidelser',
    order: 34
  },
  {
    title: 'Mellemørebetændelse',
    description: 'Akupunktur og laserbehandling for mellemørebetændelse.',
    link: '/sygdomme-lidelser/mellemoerebetaendelse',
    order: 35
  },
  {
    title: 'Meniskskader',
    description: 'Behandling og smertelindring ved meniskskader i knæet.',
    link: '/sygdomme-lidelser/meniskskader',
    order: 36
  },
  {
    title: 'Menstruations problemer',
    description: 'Hormonal balance og symptomlindring ved menstruationssmerter og PMS.',
    link: '/sygdomme-lidelser/menstruations-problemer',
    order: 37
  },
  {
    title: 'Musearm',
    description: 'Behandling af musearm og spændinger i underarm og håndled.',
    link: '/sygdomme-lidelser/albue-smerter-tennisalbue-golfalbue-eller-musearm',
    order: 38
  },
  {
    title: 'Nedsunken forfod',
    description: 'Akupunktur har en rigtig god effekt på nedsunken forfod.',
    link: '/sygdomme-lidelser/nedsunken-forfod',
    order: 39
  },
  {
    title: 'Osgood Schlatters Syndrom',
    description: 'Målrettet behandling af Osgood Schlatters syndrom hos børn og unge.',
    link: '/sygdomme-lidelser/osgood-schlatters-syndrom',
    order: 40
  },
  {
    title: 'Overgangsalder Hedeture',
    description: 'Naturlig løsning mod gener i overgangsalderen.',
    link: '/sygdomme-lidelser/overgangsalder-hedeture',
    order: 41
  },
  {
    title: 'Piskesmæld',
    description: 'Akupunktur og blide behandlinger mod piskesmæld og whiplash.',
    link: '/sygdomme-lidelser/piskesmaeld-whiplash',
    order: 42
  },
  {
    title: 'Rygestop',
    description: 'Effektiv afvænningsmetode med akupunktur og NADA.',
    link: '/sygdomme-lidelser/rygestop-tobaksafvaenning',
    order: 43
  },
  {
    title: 'Slidgigt',
    description: 'Smertelindring og øget mobilitet ved slidgigt i knæ, hofte eller ryg.',
    link: '/sygdomme-lidelser/slidgigt-i-knaeet-en-kronisk-degenerativ-sygdom',
    order: 44
  },
  {
    title: 'Sportsskader',
    description: 'Effektiv behandling af sports- og idrætsskader.',
    link: '/sygdomme-lidelser/sportsskader-idraetsskader',
    order: 45
  },
  {
    title: 'Springerknæ',
    description: 'Behandling af springerknæ og overbelastning af patellasenen.',
    link: '/sygdomme-lidelser/springerknae-jumpers-knee',
    order: 46
  },
  {
    title: 'Stress',
    description: 'Stressbehandling nær Aarhus med akupunktur og afspænding.',
    link: '/sygdomme-lidelser/stress',
    order: 47
  },
  {
    title: 'Søvnløshed',
    description: 'Effektiv behandling af søvnproblemer og insomnia.',
    link: '/sygdomme-lidelser/soevnloeshed-insomania',
    order: 48
  },
  {
    title: 'Tennisalbue',
    description: 'Akupunktur er effektiv mod tennisalbue og albuesmerter.',
    link: '/sygdomme-lidelser/albue-smerter-tennisalbue-golfalbue-eller-musearm',
    order: 49
  },
  {
    title: 'Tinnitus',
    description: 'Akupunktur hjælper mange med øresusen og tinnitus.',
    link: '/sygdomme-lidelser/tinnitus-oeresusen',
    order: 50
  }
]

const RECOMMENDED_BOOKS = [
  {
    title: "Helbred dit liv af Louise L Hay",
    description: (
      <p>
        En super bog, der hjælper med at få givet slip på dårlige oplevelser, det forkerte fokus. En hjælp til at få renset ud i dårlige tanker og energi. Du bliver hvad du tænker. Utrolig mange mennesker er blevet hjulpet fantastisk ved hjælp af denne bog. Alle har gavn af at læse den.
      </p>
    )
  },
  {
    title: "At blive rask igen – af Carl Simonton m.fl. (forlaget er Borgen)",
    description: (
      <div className="space-y-3">
        <p>
          Amerikansk psykolog der vejleder kræftramte i hvordan de kan udnytte tankens kraft. Super øvelser, fantastiske resultater og visualiseringsøvelser.
        </p>
        <p>
          I 1970’erne påviste Simonton at 90% af alle kræftramte havde været ude for en traumatisk oplevelse 6 – 18 md. før diagnosen. Begivenheden udløste en tilstand af håbløshed, afmagt, hvor sorg, bitterhed, depression eller bekymring ligesom indkapslede den kroniske STRESS – og derved dannede grobund for dårlig immunforsvar og canser.
        </p>
      </div>
    )
  },
  {
    title: "Tænk dig rask – en bog om kræft af Carsten Pedersen (forlaget Klitrose)",
    description: <p className="italic text-stone-500">Tekst på vej.</p>
  },
  {
    title: "Vejen til kraft – Fastfood for sjælen – af Barbara Berger (Beam Team books)",
    description: <p className="italic text-stone-500">Tekst på vej.</p>
  },
  {
    title: "Det Vågne menneske – en guide til sindets kraft- af Barbara Berger med Tim Ray",
    description: (
      <p>
        Barbara Berger er super god til at beskrive og fortælle hvordan tingene hænger sammen og hvordan vi hver især kan ændre vores liv ved at ændre vores tanker. Du vil modtage det du har fokus på – så sørg for at ha fokus på de rigtige ting i dit liv. Superlæsning. Kig også på{" "}
        <a href="https://www.beamteam.com" target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline font-medium">
          www.beamteam.com
        </a>{" "}
        – Barbara er mig en stor inpirationskilde.
      </p>
    )
  },
  {
    title: "Dit Liv i dine hænder af Jane Plant - Forlag Aronsen",
    description: (
      <div className="space-y-3">
        <p>
          Kig på{" "}
          <a href="https://www.janeplant.com" target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline font-medium">
            www.janeplant.com
          </a>
        </p>
        <p>
          Jane Plant er en videnskabskvinde, der på egen krop har følt hvad forkert kost gør ved en. Hun trodsede selv alle odsene, havde kun fået max. 6 måneder og leve i og nu er det ved at være rigtig mange år siden (ca. 15 år siden). Bogen har fokus på kosten alene, en vigtig del for et optimalt immunforsvar. Væk med alle de ting der svækker kroppens energi og immunforsvar. Jane anbefaler alle at droppe især mejeriprodukter, tobak, alkolhol og forurenede fødevare – bogen har rigtig mange gode forslag til kostændring og det hele er selvfølgelig videnskabeligt dokumenteret – en nem bog at læse for alle, skrevet på et letforståeligt sprog men med rigtig mange henvisninger/referenser i.
        </p>
      </div>
    )
  },
  {
    title: "Vejen til Energi af Lam Kam Chuen",
    description: (
      <p>
        En fantastik god og simpel vejledning i chi kung. Der er masser af fine tegninger og beskrivelser. Bogen findes på mange sprog også dansk.
      </p>
    )
  },
  {
    title: "Syre – base balancen",
    description: (
      <p>
        Tryk på linket og læs mere her{" "}
        <a href="https://dk4doktoren.dk/" target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline font-medium">
          https://dk4doktoren.dk/
        </a>{" "}
        skrevet af tidl. radiodoktor Carsten Vagn Hansen, noget alle burde læse.
      </p>
    )
  },
  {
    title: "Lao Tzu’s Tao Te Ching",
    description: (
      <div className="space-y-3">
        <p>
          81 fantastiske digte. Tao Te Ching har således som en af de korteste, enkleste og samtidig mest nuancerede visdomsbøger fortsat almen værdi for enhver lystlæser, central værdi for enhver der interesserer sig for taoismen, og andre behandlere, der arbejder med traditionel kinesisk medicin – samt for vores patienter.
        </p>
        <p>På dansk gendigtet af Hugo Hørlych Karlsen.</p>
      </div>
    )
  },
  {
    title: "Lykken har tusind navne af Byron Katie",
    description: (
      <div className="space-y-3">
        <p>Lev i harmoni med livet som det er.</p>
        <ol className="list-decimal pl-5 space-y-1 text-stone-600">
          <li>Er det sandt ?</li>
          <li>Can du virkelig vide at det er sandt ?</li>
          <li>Hvordan reagerer du når du tror på den tanke ?</li>
          <li>Hvem ville du være uden tanken ?</li>
        </ol>
        <p>
          Denne bog er et portræt af det vågne sind i aktion. Den er også Byron Katies respons på Tao Te Ching, den store kinesiske klassiker der er blevet kaldt den viseste bog der nogensinde er skrevet.
        </p>
      </div>
    )
  },
  {
    title: "Zhan Zhuang and the search of Wu. Skrevet af Yu Yong Nian",
    description: (
      <div className="space-y-2">
        <p>Ultimate secret of Internal Healing and Boxing Revealed.</p>
        <p>Hele professor Yu’s grund ideer med Yi Quan.</p>
      </div>
    )
  },
  {
    title: "Arbejdsglæde og begejstring. Hvor kommer det fra?",
    description: (
      <div className="space-y-2">
        <p>Stjernestunder – den kærlige dimension.</p>
        <p>Skrevet af Lisbeth G. Petersen.</p>
        <p>Hans hellighed Dalai Lama f.eks. Kunsten at leve lykkeligt.</p>
      </div>
    )
  },
  {
    title: "Kolesterol skrevet af Dr. med. Uffe Ravnskov",
    description: (
      <div className="space-y-2">
        <p>Myter og realiteter.</p>
        <p>Vidste du …. at ældre mennesker med et højt kolesteroltal lever længere end ældre med et lavt ?</p>
      </div>
    )
  },
  {
    title: "Din selvhelbredende kraft. Skrevet af Lissa Rankin",
    description: <p>Videnskabeligt bevis for at du kan helbrede dig selv.</p>
  }
]

const OEREAKUPUNKTUR_ACCORDIONS = [
  {
    title: "Den franske øreakupunktur",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Også kaldt Auriculoterapi eller Auriculomedicin er udviklet og sat i system i 1951 af den franske ingeniør, biolog og læge Paul Nogier fra Lyon. Det tog ham 15 år at kortlægge øret, dets anatomi, udvikling, nerveforsyning, refleksbanerne og nervesystemet han kaldte det ” mennesket i øret ”
        </p>
        <p>
          Hans søn læge Raphael Nogier har videreudviklet systemet så det nu står som en bæredygtig og fantastisk metode til at behandle hele mennesket.
        </p>
        <p>
          Paul Nogier kalder Auriculoterapi for en lægelig behandlingsteknik og Auriculomedicin en diagnoseteknik.
        </p>
        <p>
          Paul Nogier var i 1996 indstillet til ”Nobel Prisen i medicin” på grund af sin forskning og sine resultater, han døde desværre i juni 1996 inden prisen blev udleveret.
        </p>
        <p>
          Auriculoterapien blev i 1990 anerkendt af WHO (Verdens Sundheds Organisationen) som diagnostik- og behandlingsmiddel.
        </p>
      </div>
    )
  },
  {
    title: "Den Kinesiske Øreakupunktur",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Her har man Den Traditionelle Kinesiske Medicins historie der går flere årtusinder tilbage, dens diagnose principper, love og brug.
        </p>
        <p>
          Meridianlæren er meget vigtig for udvælgelsen af ørepunkter i en behandling og kombineres for det meste også med den almindelige krops akupunktur.
        </p>
      </div>
    )
  },
  {
    title: "NADA (National Acupuncture Detoxification Assosiation)",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Er et akupunktur system der stammer fra den kinesiske øreakupunktur, men som er blevet udviklet i USA til at afgifte og afvænne mennesker fra narkotiske rusmidler, medicin misbrug og alkoholmisbrug.
        </p>
        <p>
          Systemet stammer fra USA og har spredt sig til many lande, herunder også Skandinavien. Det har nogen afvænningsprocenter som intet andet system kan hamle op med, op mod 70% er afvænnet i løbet af et år.
        </p>
        <p>
          I USA kan kriminelle narkomaner vælge at få en behandlingsdom i stedet for at komme i fængsel. Der er lavet specielle Domstole for misbrugere.
        </p>
        <p>
          I alle misbrugssituationer er det nødvendigt, at der hos den enkelte er et ønske og en stærk motivation til at stoppe.
        </p>
        <p>
          Hver dag gives ” De 5 nåle ” i hvert øre, de sidder i ca. 45 minutter og er standard behandlingen for at hjælpe på abstinenserne, angsten, uroen, svedeturene……..
        </p>
        <p>
          Samtidig med behandlingerne er det selvfølgelig nødvendig med anden hjælp.
        </p>
        <p>
          Den sociale og psykiske side kan hjælpes med samtaleterapi i en behandlingsgruppe af misbrugere. (anonyme narkomaner og alkoholikere)
        </p>
        <p>
          I Sverige er man godt i gang med NADA systemet og har fine resultater, i Danmark er det forholdsvis nyt, men der er startet projekter op
        </p>
      </div>
    )
  }
]

const POSTUROLOGI_ACCORDIONS = [
  {
    title: "Hvad er posturologi?",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Ordet Posturologi stammer fra positur, position, postur på normal dansk ”stilling”. Posturologi er læren om og beskrivelsen af kroppens stående stilling og stillingen i bevægelse samt behandlingen af alle de forstyrrelser der er afledt af forkert stilling.
        </p>
        <p>
          Smerter skyldes ofte en forkert stilling, en holdningsforstyrrelse (holdningsanomali), muskulære skævheder, dårlig eller ringe information til hjernen og derfor en forkert regulering af vores toniske muskler.
        </p>
        <p>
          Denne fejlkommunikation gør altså skæv.
        </p>
        <p>
          Hverken vilje, massage eller træning kan regulere de toniske muskler, idet tonus er styret af hjernen (uden for viljens kontrol), udfra de oplysninger hjernen får fra hovedsageligt øjne og fødder samt de sensoriske muskler.
        </p>
      </div>
    )
  },
  {
    title: "Kroppens stilling er ubevidst",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Vore bevægelser er beviste, men vor stilling er ubevist. Derfor hjælper det ikke ret længe ad gangen at sige,: ”ret dig op, hæng nu ikke med skuldrene, hold ikke hovedet skævt, få så dine skuldre ned på plads og slap af”.
        </p>
        <p>
          Kroppens stilling udgøres i største grad af de toniske muskler, men også af nogle mekaniske forhold så som ændringer af knoglerne efter et brud, børnelammelse, medfødte asymmetriske forhold, sammenfald af ryggen p.g.a. osteoporose, scheuermann osv.
        </p>
        <p>
          Langt de fleste skævheder i kroppen er dog muskulære problemer.
        </p>
      </div>
    )
  },
  {
    title: "Årsager til posturologiske forstyrrelser",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          De fleste forstyrrelser kommer fra øjnene, enten i form af problemer med øjets protese ”Brillerne” eller fra forstyrrelser i vores kæbe – bidefunktion ofte p.g.a. tandprotese, fyldninger i tænderne, stifttænder, broer m.m.
        </p>
        <p>
          En anden årsag kan komme nede fra vore fødder, hvis føddernes trykfølere til regulering af vores holdning svigter os, enten på grund forkerte indlæg i skoene, slidte sko eller uheld med fødderne.
        </p>
        <p>
          Skæv fodstilling kan f.eks. være årsagen til en diskosprolaps i ryggen.
        </p>
      </div>
    )
  },
  {
    title: "Problemer ved en posturologisk forstyrrelse",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Posturologiske problemer ses ofte i form af smerter i hoved, rygproblemer som iskias eller lumbago og discusprolaps, smerter i skuldre, hofte, lænd, balder, lyske, knæ, fødder. Problemer med achillessene, hælspore samt som Whiplash syndrom (piskesmæld i nakken), muskelgigt.
        </p>
        <p>
          Kognitive problemer kan være indlæringsproblemer som dysleksi (ordblindhed), Sprogproblemer, hyperventilation, hukommelses problemer både med hensyn til sted og tidsorientering, hysteri, nervesammenbrud og træthed. Angst kan være en posturologisk forstyrrelse af øjnene, det samme gælder for køre-, luft- og søsyge.
        </p>
        <p>
          En Posturolog har sagt,” at små problemer i bid/tænder, øjne og fødder udløser store problemer andre steder ”.
        </p>
      </div>
    )
  },
  {
    title: "Behandling af posturologisk forstyrrelse",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Selve behandlingen af en posturologisk forstyrrelse afhænger lidt af årsagen til problemet.
        </p>
        <p>
          Årsagen findes ved at lave forskellige tests og hvis det f.eks. er føddernes stilling, der er årsagen, ændrer man den med nogle meget små indlæg og en lille microchip ”Neurostab” til at genetablere den naturlige reguleringsmekanisme fra fødderne til hjernen.
        </p>
        <p>
          Der skal måske også laves lidt øreakupunktur for hofte, skuldre, kæbe eller øjne. Er problemet øjnene, skal brillerne måske tjekkes efter kæben og bidfunktionen skal også tjekkes efter, man vil også benytte øreakupunktur.
        </p>
        <p>
          Smerter forårsaget af øjne og kæbe/bidfunktionen kan ofte forsvinde i løbet af ca. 30 sekunder, hvis de kun skyldes muskelskævhed, men selve behandlingen at rette skævheden op kan godt tage 1 eller 2 år alt afhængig af skævhedsgraden i rygsøjlen.
        </p>
      </div>
    )
  }
]

const MAGNETFELT_ACCORDIONS = [
  {
    title: "Cellernes stofskifte påvirkes af Magnetfelt terapi",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Det menneskelige legeme består af celler, millioner af celler. Vores celler er omgivet af en cellemembran, og det er gennem cellemembranen, at cellen får næring, f.eks. ilt, vitaminer, mineraler, kulhydrater, protein osv. Selve cellestofskiftet er passagen af disse various stoffer, ind med næringsstofferne som giver ilt/energi og ud med affaldsstofferne. Styringen er bl.a. elektromagnetisk betinget.
        </p>
        <p>
          Forskere har fundet ud af at de forskellige cellegrupper i kroppen påvirkes forskelligt af magnetfelternes frekvens. Enhver cellegruppe har helt sin egen elektromagnetiske frekvens. Derfor er den pulserende magnetfeltterapi langt mere effektiv end den traditionelle, baseret på et stationært nord-syd magnetfelt.
        </p>
        <p>
          Ifølge forskerne er den kraftigste måde at påvirke kroppen ved at påvirke med frekvenser.
        </p>
        <p>
          Magnetfelterne påvirker jernatomerne i blodet. Herved styrkes først og fremmest blodcirkulationen og blodets evne til at bringe ilt med rundt i kroppen. Mange oplever en dejlig varm fornemmelse sprede sig rundt i kroppen efter få minutters behandling.
        </p>
        <p>
          Vi har set benbrud hele på den halve tid, p.g.a. den forbedrede blodcirkulation.
        </p>
      </div>
    )
  },
  {
    title: "Skadelige magnetfelter",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Der findes også menneskeskabte og skadelige magnetfelter, som påvirker vores cellers naturlige cellestofskifte. Disse skadelige felter går ind og forstyrrer og skader kroppens cellestofskifte by at blokere og overdøve vores naturlige felter og svingninger. Skadelige felter kan komme fra mobiltelefoner, mikrobølgeovne, TV apparater og højspændingsledninger m.m.
        </p>
        <p>
          Vi benytter magnetfelt terapi sammen med akupunktur ved en række lidelser og med ganske god effekt.
        </p>
      </div>
    )
  }
]

const TKM_ACCORDIONS = [
  {
    title: "Hvad er Yin og Yang?",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Yin og Yang er de to modstridende, men uadskillelige kræfter i universet og i den menneskelige krop. Alt i naturen har både en Yin- og en Yang-side.
        </p>
        <p>
          <strong>Yin</strong> repræsenterer det feminine, koldere, rolige, mørke, fugtige og opbyggende. <strong>Yang</strong> repræsenterer det maskuline, varme, aktive, lyse, tørre og bevægende.
        </p>
        <p>
          I Traditionel Kinesisk Medicin (TKM) opstår sundhed og velvære, når Yin og Yang er i perfekt dynamisk balance. Når den ene overskygger den anden eller svækkes, opstår der ubalancer, som kan føre til symptomer og sygdom.
        </p>
      </div>
    )
  },
  {
    title: "Se på Yin - Yang tegnet",
    description: (
      <div className="space-y-4 text-stone-600 leading-relaxed">
        <div className="my-2 text-center">
          <img
            src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/yingyangstor.jpg"
            alt="Yin og Yang symbolet"
            className="max-h-64 w-auto mx-auto object-contain rounded-sm border border-stone-100 shadow-xs"
          />
          <p className="text-xs text-stone-500 italic mt-2">Det klassiske Yin - Yang symbol (Taijitu)</p>
        </div>
        <p>
          Det velkendte Yin - Yang tegn (Taijitu) illustrerer, hvordan kræfterne flyder over i hinanden. Cirklen symboliserer helheden, mens den sorte og hvide dråbe viser den stadige vekselvirkning.
        </p>
        <p>
          Læg mærke til prikken med modsat farve inde i hver halvdel: Det viser, at intet er 100% Yin eller 100% Yang – i det mørkeste Yin findes spiren til Yang, og i det stærkeste Yang findes spiren til Yin.
        </p>
      </div>
    )
  },
  {
    title: "Energibaner (Meridianer)",
    description: (
      <div className="space-y-4 text-stone-600 leading-relaxed">
        <div className="my-2 text-center">
          <img
            src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/energibaner.jpg"
            alt="Kroppens energibaner og meridianer"
            className="max-h-64 w-auto mx-auto object-contain rounded-sm border border-stone-100 shadow-xs"
          />
          <p className="text-xs text-stone-500 italic mt-2">Kroppens 12 primære meridianbaner og energicirkulation</p>
        </div>
        <p>
          I kroppen strømmer livsenergien (Chi) gennem et usynligt netværk af energibaner, kaldet meridianer. Der findes 12 primære meridianer, som hver især er knyttet til et bestemt organ (f.eks. Lunge, Tyktarm, Mave, Milt, Hjerte, Nyre m.fl.).
        </p>
        <p>
          Når energien flyder uforstyrret gennem banerne, fungerer kroppen optimalt. Hvis der opstår en blokering eller stagnation i en energibane, vil organet eller vævet langs banen lide under det, og smerte eller funktionsforstyrrelse opstår.
        </p>
      </div>
    )
  },
  {
    title: "Årsager til sygdom og ubalancer",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Inden for TKM opdeles årsagerne til ubalancer og sygdom i tre hovedkategorier:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-stone-600">
          <li><strong>Ydre klimatiske faktorer (De 6 onde faktorer):</strong> Vind, Kulde, Sommerhede, Fugt, Tørhed og Ild/Varme, som kan trænge ind i kroppen, hvis immunforsvaret (Wei Chi) er svækket.</li>
          <li><strong>Indre følelsesmæssige faktorer (De 7 følelser):</strong> Vrede, Glæde/overbegejstring, Bekymring/spekulation, Sorg, Frygt, Chok og Angst. Vedvarende eller intense følelser skader organernes energi.</li>
          <li><strong>Andre livsstilsfaktorer:</strong> Overarbejde uden tilstrækkelig hvile, for lidt eller forkert motion, usund kost, uheld/traumer samt forkert livsførelse.</li>
        </ul>
      </div>
    )
  },
  {
    title: "Forebyggelse og behandling",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          TKM fokuserer altid på at behandle både roden til problemet (Ben) og selve symptomet (Biao). Filosofien er frem for alt forebyggende – det handler om at vedligeholde sit helbred og balancere kroppen, før sygdommen når at rodfæste sig.
        </p>
        <p>
          Behandlingen tilpasses den enkelte og kombinerer akupunktur, urter, kost, moxa, kopning samt energitræning som Chi Kung og Tai Chi for at genskabe den naturlige balance.
        </p>
      </div>
    )
  }
]

const NEWS_ACCORDIONS = [
  {
    title: "Tilbage efter sommerferie og holdstart",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Så er vi begge tilbage på klinikken efter en dejlig sommerferie. Chi kung holdtræning starter mandag 14. september kl. 17.00 og tirsdag den 15. september kl. 10.00.
        </p>
        <p>
          Vi glæder os til at se dig på klinikken.
        </p>
      </div>
    )
  },
  {
    title: "Chi-Kung Holdtræning & Workshops i Gjerrild",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          <strong>Nye datoer for holdtræning:</strong>
        </p>
        <p>
          Holdtræning starter mandag den 14. september kl. 17.00 - 18.15 frem til 14. december.
        </p>
        <p>
          Holdtræning starter tirsdag den 15. september kl. 10.00 - 11.15 frem til 15. december.
        </p>
        <p>
          <strong>Pris:</strong> kr. 1300,- inkl. moms.
        </p>
        <p>
          <strong>Sted:</strong> Helseklinikken i Gjerrild, i vores egen sal.
        </p>
        <p>
          <strong>Tilmelding:</strong> Email{" "}
          <a href="mailto:info@helselarsen.dk" className="text-forest-700 hover:underline font-medium">
            info@helselarsen.dk
          </a>{" "}
          eller tlf.{" "}
          <a href="tel:86384117" className="text-forest-700 hover:underline font-medium">
            (+45) 86 38 41 17
          </a>
          . Ring og hør nærmere.
        </p>
      </div>
    )
  },
  {
    title: "Seneste nyt & tiltag fra Helseklinikken",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Hold dig opdateret med vores seneste nyheder, tiltag og oplysninger fra Helseklinikken i Gjerrild her og nu.
        </p>
        <p>
          Vi tilbyder rådgivning og akupunktur for et bredt udvalg af lidelser og opdaterer løbende vores viden og metoder.
        </p>
      </div>
    )
  },
  {
    title: "Åndedrætsworkshops & inspiration",
    description: (
      <div className="space-y-3 text-stone-600 leading-relaxed">
        <p>
          Lars og Jørgen afholder og deltager løbende i temadage og workshops med internationale eksperter inden for åndedræt, Chi Kung og energimedicin for at sikre den mest optimale behandling af vores patienter.
        </p>
      </div>
    )
  }
]

const SLIDESHOWS = {
  'kinesisk-urtemedicin': [
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/urt1.jpg',
      alt: 'Kinesisk urtemedicin 1',
      caption: 'Tørrede kinesiske urter og planterødder'
    },
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/urt2.jpg',
      alt: 'Kinesisk urtemedicin 2',
      caption: 'Traditionel tilberedning af urtemedicin'
    },
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/urt3.jpg',
      alt: 'Kinesisk urtemedicin 3',
      caption: 'Nøje afbalancerede urteblandinger'
    }
  ]
}

function CmsAccordionList({ items, defaultOpenIndex = null }) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex)

  if (!items || items.length === 0) return null

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const isOpen = openIndex === index
        const rawContent = item.content ?? item.description
        const isHtmlDesc = typeof rawContent === 'string'

        return (
          <div
            key={item.id ?? index}
            className="border border-forest-100 bg-white/50 transition hover:bg-white/80 rounded-sm overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="w-full flex items-center justify-between p-4 sm:p-5 text-left font-medium text-stone-800 hover:text-forest-900 transition-colors focus:outline-none cursor-pointer"
            >
              <span className="font-serif text-base sm:text-lg text-forest-900 font-medium">
                {item.title}
              </span>
              <i
                className={`fa-solid fa-chevron-down text-sm text-forest-600 transition-transform duration-200 shrink-0 ml-3 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <div
              className={`grid transition-all duration-200 ease-in-out ${
                isOpen
                  ? 'grid-rows-[1fr] opacity-100 border-t border-forest-50/50'
                  : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="p-4 sm:p-5 text-stone-600 text-sm sm:text-base leading-relaxed bg-stone-50/20">
                  {isHtmlDesc ? (
                    <RichContent html={rawContent} className="prose max-w-none text-stone-600" />
                  ) : (
                    rawContent
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function StaticCmsPage({ slug, basePath = '' }) {
  const { patienthistorier, getPage, behandlingsmetoder, sygdommeLidelser, getAccordionsForPage } = useSiteData()
  const page = getPage(slug)
  const [currentSlide, setCurrentSlide] = useState(0)

  const pageAccordionsFromCms = useMemo(() => {
    return getAccordionsForPage ? getAccordionsForPage(slug) : []
  }, [getAccordionsForPage, slug])

  const newsAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return NEWS_ACCORDIONS
  }, [pageAccordionsFromCms])

  const magnetAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return MAGNETFELT_ACCORDIONS
  }, [pageAccordionsFromCms])

  const posturologiAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return POSTUROLOGI_ACCORDIONS
  }, [pageAccordionsFromCms])

  const herbalAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return HERBAL_ACCORDIONS
  }, [pageAccordionsFromCms])

  const oereAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return OEREAKUPUNKTUR_ACCORDIONS
  }, [pageAccordionsFromCms])

  const tkmAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return TKM_ACCORDIONS
  }, [pageAccordionsFromCms])

  const bookAccordions = useMemo(() => {
    if (pageAccordionsFromCms.length > 0) return pageAccordionsFromCms
    return RECOMMENDED_BOOKS
  }, [pageAccordionsFromCms])

  const customPageAccordions = useMemo(() => {
    const knownAccordionSlugs = [
      'nyheder',
      'magnetfelt-terapi',
      'posturologi',
      'kinesisk-urtemedicin',
      'oereakupunktur',
      'traditionel-kinesisk-medicin-t-k-m',
      'anbefalet-laesning'
    ]
    if (knownAccordionSlugs.includes(slug)) {
      return []
    }
    return pageAccordionsFromCms
  }, [pageAccordionsFromCms, slug])

  useEffect(() => {
    setCurrentSlide(0)
  }, [slug])

  useEffect(() => {
    const slides = SLIDESHOWS[slug]
    if (!slides) return

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev >= slides.length - 1 ? 0 : prev + 1))
    }, 5000)

    return () => clearInterval(timer)
  }, [slug])

  const relatedStories = useMemo(() => {
    if (!page) return []

    const mapping = {
      akupunktur: 'akupunktur',
      behandlinger: 'akupunktur',
      rygsmerter: 'rygsmerter',
      kol: 'kol',
      sportsskader: 'sportsskader',
      diskusprolaps: 'rygsmerter',
      'iskias-smerter': 'rygsmerter',
      'piskesmaeld-whiplash': 'rygsmerter',
      'knae-smerter': 'sportsskader',
      behandlingsforloeb: 'rygsmerter',
    }

    const target = mapping[slug]
    if (!target) return []

    return patienthistorier.filter((story) => (story?.terms?.treatment || []).includes(target))
  }, [patienthistorier, slug, page])

  if (!page) return <NotFoundPage />

  const subtitle = excerptFromHtml(getCmsPageText(page), 190)
  const image = page.image?.[0] || (
    slug === 'anbefalet-laesning' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/langtlivoglykke_5.png' :
    slug === 'chi-kung-energitraening' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/mennesker1_1.jpg' :
    slug === 'chi-kung-workshop' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/img-1800-scaled.jpg' :
    slug === 'oereakupunktur' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/untitled2_3.jpg' :
    slug === 'posturologi' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/posturologi.png' :
    slug === 'nyheder' ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/image_786279da.png' :
    slug === 'magnetfelt-terapi' ? null :
    null
  )

  const hasSidebar = Boolean(SLIDESHOWS[slug] || (image && slug !== 'kol' && slug !== 'magnetfelt-terapi' && slug !== 'akupunktur' && slug !== 'tai-chi-chuan' && slug !== 'posturologi' && slug !== 'laserterapi' && slug !== 'reaktioner' && slug !== 'behandlingsmetoder'))

  const isNoHeroDesc =
    slug === 'priser' ||
    slug === 'nyheder' ||
    slug === 'gode-raad' ||
    slug === 'sygdomme-lidelser' ||
    slug === 'reaktioner' ||
    slug === 'behandlingsforloeb' ||
    slug === 'behandlingsmetoder' ||
    slug === 'anbefalet-laesning' ||
    basePath.includes('behandlingsmetoder') ||
    [
      'klassisk-kinesisk-akupunktur',
      'tkm-traditionel-kinesisk-medicin',
      'chi-kung-energitraening',
      'chi-kung-workshop',
      'tai-chi-chuan',
      'oereakupunktur',
      'posturologi',
      'laserterapi',
      'magnetfelt-terapi',
      'tens',
      'tns',
      'e-f-t-banke-akupunktur',
      'vaegttab-paa-den-nemme-maade',
      'klinikkens-baggrund',
      'klinikkens-historie',
      'joergens-baggrund',
      'lars-baggrund',
      'lars-i-larsen',
      'joergen-larsen',
      'om-klinikken',
      'om-os'
    ].includes(slug)

  const defaultHeroImages = {
    'anbefalet-laesning': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/anbefalet-la-sning.png',
    'gode-raad': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/gode-ra-d.png',
    'klinikkens-baggrund': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/klinikkens-baggrund_1.png',
    'klinikkens-historie': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/klinikkens-historie.png',
    'joergens-baggrund': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/ja-rgens-historie.png',
    'lars-baggrund': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/lars-baggrund.png',
    'traditionel-kinesisk-medicin-t-k-m': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/traditionel-kinesisk-medicin.jpg',
    'akupunktur': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/klassisk-kinesisk-akupunktur2_1.png',
    'kinesisk-urtemedicin': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/kinesisk-urtemedicin-kinesisk-apotek_1.jpg',
    'chi-kung-energitraening': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/chikung-energi.png',
    'chi-kung-workshop': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/chikung-workshop.png',
    'tai-chi-chuan': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/taichi-chuan.jpg',
    'oereakupunktur': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/a-reakku.png',
    'posturologi': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/posturologi.png',
    'laserterapi': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/laserbehandling.jpg',
    'magnetfelt-terapi': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/magnetfelt.png',
    'tens': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/tens.png',
    'tns': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/tens.png',
    'e-f-t-banke-akupunktur': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/eft.png',
    'vaegttab-paa-den-nemme-maade': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/va-gttab-1.jpg',
    'sygdomme-lidelser': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/sygdomme-lidelser.jpg',
    'who-anbefaler-akupunktur': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/who-anbefaler.jpg',
    'reaktioner': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/reaktioner.png',
    'behandlingsforloeb': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/behandlingsforla-b.png',
    'priser': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/priser.jpg',
    'kontakt': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/kontakt.jpg',
    'nyheder': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/nyheder.png',
    'patienthistorier': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg',
    'rygsmerter': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/billede-af-ryg-img-1399-scaled.jpg',
    'kol': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/jens-peter-albrechtsen-large.png',
    'sportsskader': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/atv-tur-juli-2005-016-2-e1723709653503.jpg',
    'sportsskader-idraetsskader': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/atv-tur-juli-2005-016-2-e1723709653503.jpg'
  }

  const heroImageSrc = defaultHeroImages[slug] || (basePath.includes('sygdomme-lidelser') ? defaultHeroImages['sygdomme-lidelser'] : (page.hero_image?.[0] || page.image?.[0] || null))
  const heroBgPosition = slug === 'laserterapi' ? 'center bottom' : 'center center'

  const heroStyle = heroImageSrc
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(16, 32, 22, 0.15) 0%, rgba(16, 32, 22, 0.15) 100%), url('${heroImageSrc.includes('/cdn-cgi/image/') ? heroImageSrc : heroImageSrc.startsWith('https://assets.auxodev.dk/') ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${heroImageSrc.replace('https://assets.auxodev.dk/', '')}` : heroImageSrc}')`,
        backgroundSize: 'cover',
        backgroundPosition: heroBgPosition,
        backgroundRepeat: 'no-repeat'
      }
    : {}

  return (
    <>
      <SeoManager
        title={page.meta_title || page.title}
        description={page.meta_description || subtitle}
        image={image}
        canonical={`https://helselarsen.dk/${basePath}${slug === 'index' ? '' : `${slug}/`}`}
      />

      <main>
        <section 
          className="border border-forest-900 bg-forest-950 py-20 sm:py-28 lg:py-36 min-h-[380px] sm:min-h-[460px] lg:min-h-[540px] flex items-center"
          style={heroStyle}
        >
          <Container className="w-full">
            <PageHero page={page} kicker={page.title} description={slug === 'kontakt' ? "Du er altid velkommen til at kontakte os med spørgsmål, eller hvis du ønsker at bestille en tid. Vi glæder os til at hjælpe dig." : (isNoHeroDesc ? null : subtitle)} actions={page.slug?.da === 'kontakt' ? [{ label: 'Ring nu', href: 'tel:86384117' }] : [{ label: 'Kontakt os', href: '/kontakt' }, { label: 'Til patienthistorier', href: '/patienthistorier', variant: 'secondary' }]} />
          </Container>
        </section>

        <section className="py-10 lg:py-16">
          <Container>
            <div className={hasSidebar ? "grid gap-8 lg:grid-cols-[1fr_0.45fr] lg:items-start" : slug === 'akupunktur' ? "w-full" : "w-full max-w-4xl mx-auto"}>
              <Card className="space-y-5 max-w-full overflow-hidden text-left">
                {slug === 'behandlingsmetoder' || slug === 'behandlinger' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={(page.content || '').replace(/<div class="grid[\s\S]*$/i, '')}
                      className="prose max-w-none"
                    />
                    <div className="grid gap-6 md:grid-cols-2 mt-8">
                      {(behandlingsmetoder && behandlingsmetoder.length > 0 ? behandlingsmetoder : FALLBACK_BEHANDLINGSMETODER).map((item, idx) => (
                        <div
                          key={item.id || item.slug?.da || idx}
                          className="border border-forest-100 p-5 bg-white transition hover:border-forest-300"
                        >
                          <h3 className="font-serif text-lg font-bold text-forest-900">
                            {item.link ? (
                              <Link
                                to={item.link}
                                className="hover:text-forest-700 hover:underline"
                              >
                                {item.title}
                              </Link>
                            ) : (
                              item.title
                            )}
                          </h3>
                          {item.description && (
                            <p className="text-sm text-stone-600 mt-2">{item.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : slug === 'sygdomme-lidelser' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={(page.content || '').replace(/<div class="grid[\s\S]*$/i, '')}
                      className="prose max-w-none"
                    />
                    <div className="grid gap-6 md:grid-cols-2 mt-8">
                      {(sygdommeLidelser && sygdommeLidelser.length > 0 ? sygdommeLidelser : FALLBACK_SYGDOMME_LIDELSER).map((item, idx) => (
                        <div
                          key={item.id || item.slug?.da || idx}
                          className="border border-forest-100 p-5 bg-white transition hover:border-forest-300"
                        >
                          <h3 className="font-serif text-lg font-bold text-forest-900">
                            {item.link ? (
                              <Link
                                to={item.link}
                                className="hover:text-forest-700 hover:underline"
                              >
                                {item.title}
                              </Link>
                            ) : (
                              item.title
                            )}
                          </h3>
                          {item.description && (
                            <p className="text-sm text-stone-600 mt-2">{item.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : slug === 'anbefalet-laesning' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={
                        page.content ||
                        '<h2 class="font-serif text-xl sm:text-2xl font-bold text-stone-800">Inspirerende bøger for positiv indstilling</h2><p class="text-base leading-7 text-stone-600 sm:text-lg">Her kommer nogle gode bøger som vi syntes du skal læse. Du kan påvirke din underbevidsthed og dit sind på bedste vis ved at læse de rigtige bøger – Du får herved værktøjer til at få en mere positiv indstilling til alt i livet. Du er din egen lykkesmed, din computer kører jo kun med det input du giver den. Bøger, musik og bevægelse er en fantastisk måde at hjælpe din krop og sind til en bedre tilstand. Vi bør læse og lytte dagligt til inspirerende og positive kilder.</p>'
                      }
                      className="prose max-w-none text-base leading-7 text-stone-600 sm:text-lg"
                    />
                    
                    <div className="mt-6">
                      <CmsAccordionList items={bookAccordions} />
                    </div>
                  </div>
                ) : slug === 'traditionel-kinesisk-medicin-t-k-m' ? (
                  <div className="space-y-6">
                    <RichContent html={page.content || ''} className="prose max-w-none" />
                    <div className="space-y-4 mt-8">
                      <h3 className="font-serif text-xl font-bold text-forest-900 border-b border-forest-100 pb-2">
                        Underpunkter i Traditionel Kinesisk Medicin
                      </h3>
                      <CmsAccordionList items={tkmAccordions} />
                    </div>
                  </div>
                ) : slug === 'akupunktur' ? (
                  <div className="space-y-6">
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.45fr] lg:items-start">
                      <RichContent html={page.content || ''} className="prose max-w-none" />
                      <div className="bg-stone-50/80 p-4 border border-stone-200/80 rounded-sm text-center shadow-xs">
                        <img
                          src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/akuhjaelper_1.png"
                          alt="Kinesiske tegn - Akupunktur hjælper dem der har behov"
                          className="w-full h-auto object-contain mx-auto rounded-xs"
                        />
                        <p className="text-xs font-serif text-forest-900 font-semibold mt-3">
                          Akupunktur hjælper dem der har behov
                        </p>
                      </div>
                    </div>
                  </div>
                ) : slug === 'oereakupunktur' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={(page.content || '').replace(/<ul>[\s\S]*?<\/ul>/g, '')}
                      className="prose max-w-none"
                    />
                    
                    <div className="space-y-4 mt-8">
                      <CmsAccordionList items={oereAccordions} />
                    </div>

                    <div className="mt-8 text-center pt-4 border-t border-stone-100">
                      <img
                        src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/oereakupunktur-joergen-behandler-en-patient.jpg"
                        alt="Jørgen Larsen giver øreakupunktur"
                        className="max-h-60 w-full max-w-xl mx-auto object-cover rounded-sm border border-stone-100 shadow-xs"
                      />
                      <p className="text-xs text-stone-500 italic mt-2">Jørgen Larsen giver øreakupunktur</p>
                    </div>
                  </div>
                ) : slug === 'tai-chi-chuan' ? (
                  <div className="space-y-6">
                    <RichContent html={page.content || ''} className="prose max-w-none" />
                    <div className="mt-8 text-center pt-4 border-t border-stone-100">
                      <img
                        src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/taichi-chuan.jpg"
                        alt="Tai Chi Chuan"
                        className="max-h-72 w-full max-w-xl mx-auto object-cover rounded-sm border border-stone-100 shadow-xs"
                      />
                    </div>
                  </div>
                ) : slug === 'laserterapi' ? (
                  <div className="space-y-6">
                    <RichContent html={page.content || ''} className="prose max-w-none" />
                    <div className="mt-8 text-center pt-4 border-t border-stone-100">
                      <img
                        src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/laserbehandling.jpg"
                        alt="Jørgen Larsen giver laserbehandling"
                        className="max-h-72 w-full max-w-xl mx-auto object-cover rounded-sm border border-stone-100 shadow-xs"
                      />
                      <p className="text-xs text-stone-500 italic mt-2">Jørgen Larsen giver laserbehandling</p>
                    </div>
                  </div>
                ) : slug === 'vaegttab-paa-den-nemme-maade' ? (
                  <div className="space-y-6">
                    <RichContent html={page.content || ''} className="prose max-w-none" />
                  </div>
                ) : slug === 'kinesisk-urtemedicin' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={(page.content || '').replace(/<ul>[\s\S]*?<\/ul>/g, '')}
                      className="prose max-w-none"
                    />
                    
                    <div className="space-y-4 mt-8">
                      <CmsAccordionList items={herbalAccordions} />
                    </div>
                  </div>
                ) : slug === 'posturologi' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={page.content || ''}
                      className="prose max-w-none"
                    />
                    
                    <div className="space-y-4 mt-8">
                      <CmsAccordionList items={posturologiAccordions} />
                    </div>

                    {image && (
                      <div className="mt-8 text-center pt-4 border-t border-stone-100">
                        <img
                          src={image}
                          alt={page.title}
                          className="max-h-72 w-full max-w-xl mx-auto object-cover rounded-sm border border-stone-100 shadow-xs"
                        />
                      </div>
                    )}
                  </div>
                ) : slug === 'magnetfelt-terapi' ? (
                  <div className="space-y-6">
                    <RichContent
                      html={page.content || ''}
                      className="prose max-w-none"
                    />
                    
                    <div className="space-y-4 mt-8">
                      <CmsAccordionList items={magnetAccordions} />
                    </div>
                  </div>
                ) : slug === 'nyheder' ? (
                  <div className="space-y-6">
                    {page.content ? (
                      <RichContent html={page.content} className="prose max-w-none text-stone-600 text-base leading-relaxed" />
                    ) : (
                      <p className="text-stone-600 text-base leading-relaxed">
                        Hold dig opdateret med de seneste nyheder, tiltag og oplysninger fra Helseklinikken i Gjerrild.
                      </p>
                    )}
                    <div className="space-y-4 mt-6">
                      <CmsAccordionList items={newsAccordions} defaultOpenIndex={0} />
                    </div>
                  </div>
                ) : slug === 'kontakt' ? (
                  <div className="space-y-8">
                    <div className="grid gap-8 lg:grid-cols-2">
                      <div>
                        <RichContent
                          html={page.content || ''}
                          className="prose max-w-none"
                        />
                      </div>
                      <div>
                        <ContactForm />
                      </div>
                    </div>
                    <div className="w-full h-[450px] overflow-hidden rounded-sm border border-stone-100 shadow-xs">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d16580.402696490284!2d10.812079841613699!3d56.50969320273691!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464e808e4bbeaef7%3A0x36a84286906634c4!2sHealth%20Clinic%20in%20Gjerrild%20I%20%2F%20S!5e0!3m2!1sen!2sdk!4v1781601774313!5m2!1sen!2sdk"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Helseklinikken i Gjerrild på kort"
                      />
                    </div>
                  </div>
                ) : slug === 'who-anbefaler-akupunktur' ? (
                  <RichContent
                    html={page.content || ''}
                    className="prose max-w-none [&_ul]:columns-1 [&_ul]:sm:columns-2 [&_ul]:gap-x-8 [&_ul]:my-4 [&_ul]:pl-5 [&_li]:break-inside-avoid [&_li]:!mb-0 [&_li]:!pb-0 [&_li]:!mt-0 [&_li]:!pt-0 [&_li]:!my-0 [&_li]:!py-0 [&_li]:!m-0 [&_li]:!p-0 [&_li]:leading-snug"
                  />
                ) : (
                  <>
                    <RichContent
                      html={page.content || ''}
                      className="prose max-w-none"
                    />
                    {customPageAccordions.length > 0 && (
                      <div className="mt-8 pt-6 border-t border-forest-50/50">
                        <CmsAccordionList items={customPageAccordions} />
                      </div>
                    )}
                    {!hasSidebar && image && (
                      <div className="mt-8 text-center pt-4 border-t border-stone-100">
                        <img
                          src={image}
                          alt={page.title || "Reaktioner på akupunkturbehandling"}
                          className="h-auto w-full max-w-xl mx-auto object-cover rounded-sm border border-stone-100 shadow-xs"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>

              {SLIDESHOWS[slug] ? (
                <div className="space-y-3">
                  <div className="relative group overflow-hidden rounded-sm border border-stone-100 shadow-sm bg-white">
                    <div className="relative h-[24rem] w-full overflow-hidden">
                      {SLIDESHOWS[slug].map((slide, i) => (
                        <div
                          key={i}
                          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                            currentSlide === i ? 'opacity-100 z-10' : 'opacity-0 z-0'
                          }`}
                        >
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${slide.src.replace(
                              'https://assets.auxodev.dk/',
                              ''
                            )}`}
                            alt={slide.alt}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ))}

                      {/* Navigation Arrows */}
                      <button
                        onClick={() =>
                          setCurrentSlide((prev) => (prev === 0 ? SLIDESHOWS[slug].length - 1 : prev - 1))
                        }
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-forest-900 w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 focus:outline-none"
                        aria-label="Forrige billede"
                      >
                        <i className="fa-solid fa-chevron-left" />
                      </button>
                      <button
                        onClick={() =>
                          setCurrentSlide((prev) => (prev === SLIDESHOWS[slug].length - 1 ? 0 : prev + 1))
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-forest-900 w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 focus:outline-none"
                        aria-label="Næste billede"
                      >
                        <i className="fa-solid fa-chevron-right" />
                      </button>

                      {/* Indicator Dots */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                        {SLIDESHOWS[slug].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentSlide(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                              currentSlide === i ? 'bg-forest-700 w-6' : 'bg-white/60 hover:bg-white'
                            }`}
                            aria-label={`Gå til billede ${i + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  {SLIDESHOWS[slug][currentSlide]?.caption && (
                    <p className="text-sm text-stone-500 italic text-center px-2">
                      {SLIDESHOWS[slug][currentSlide].caption}
                    </p>
                  )}
                </div>
              ) : hasSidebar ? (
                <div className="space-y-3">
                  <Card className="overflow-hidden p-0 border border-stone-100 shadow-xs bg-white text-center">
                    <img
                      src={image}
                      alt={
                        slug === 'anbefalet-laesning'
                          ? "Kinesisk plakat - langt liv og lykke"
                          : slug === 'oereakupunktur'
                          ? "Mennesket i øret"
                          : slug === 'who-anbefaler-akupunktur'
                          ? "Gunnar Hansen"
                          : page.title
                      }
                      className="h-auto w-full object-cover"
                    />
                  </Card>
                  {slug === 'who-anbefaler-akupunktur' && (
                    <Card className="overflow-hidden p-0 border border-stone-100 shadow-xs bg-white text-center mt-3">
                      <img
                        src="https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/image_2.png"
                        alt="Gunnar Hansen akupunktur og magnetterapi"
                        className="h-auto w-full object-cover"
                      />
                    </Card>
                  )}
                </div>
              ) : null}
            </div>

            <RelatedStories slug={slug} stories={patienthistorier} />
          </Container>
        </section>
      </main>
    </>
  )
}
